/**
 * Unread message count for the signed-in persona.
 *
 * WHY THIS EXISTS
 * ---------------
 * The count was already being computed — both messages screens reduce their
 * conversation list into a `totalUnread` — but it never left the screen, so the
 * Messages tab had no badge and you only discovered a new message by opening
 * the thread. `tabBarBadge` appeared nowhere in the codebase.
 *
 * WHY IT REUSES useConversations RATHER THAN QUERYING
 * --------------------------------------------------
 * It calls `useConversations` with exactly the arguments the messages screen
 * passes — the same profile id, the same side — so React Query resolves the
 * SAME key (`['conversations', <id>, <side>]`) and the badge shares one cache
 * entry with the list instead of opening a second request that could disagree
 * with it. That is also why the id comes from the profile store here: it is the
 * selector the screens use, and a different source (e.g. the server-fetched
 * investor profile row) would be a different value at mount and split the cache.
 *
 * The selectors return the ID, not the profile object — a primitive, so the
 * subscribing layout does not re-render on unrelated profile edits.
 */

import { useMemo } from 'react';
import { useConversations } from '@/lib/supabase/api';
import { mapConversationRow } from '@/lib/supabase/messaging-mappers';
import { useProfileStore } from '@/lib/state/profile-store';
import { useSubscriptionStore } from '@/lib/state/subscription-store';
import { mode } from '@/lib/config/app-mode';

/**
 * Total unread messages across the persona's conversations.
 *
 * Returns 0 rather than a count when messaging is not the user's to open:
 *
 *  - **Sample/demo mode** — the mock threads live inside the screen files and
 *    carry invented unread counts. Badging the tab from real server state while
 *    the list shows fixtures would have the two disagree on screen.
 *  - **No messaging entitlement** — a badge is an instruction to go somewhere,
 *    and that screen would only show an upgrade wall. Note `hasFeature` is
 *    deliberately OPTIMISTIC while the subscription loads (the loading-gate
 *    invariant), so during the login window this can briefly return a count
 *    that then clears — a false positive, which is the direction that invariant
 *    chooses on purpose.
 *
 * @param side Which persona's inbox to count — decides both which profile id to
 *   resolve and which of the two split unread counters on the row applies.
 */
export function useUnreadMessageCount(side: 'startup' | 'investor'): number {
  const startupProfileId = useProfileStore((s) => s.startupProfile?.id);
  const investorProfileId = useProfileStore((s) => s.investorProfile?.id);
  const hasMessaging = useSubscriptionStore((s) => s.hasFeature('direct_messaging'));

  const profileId = side === 'startup' ? startupProfileId : investorProfileId;
  const { data: rows } = useConversations(profileId, side);

  return useMemo(() => {
    if (mode.showSampleData || !hasMessaging) return 0;
    return (rows ?? []).reduce((sum, row) => sum + mapConversationRow(row).unreadCount, 0);
  }, [rows, hasMessaging]);
}
